import React, { useState } from 'react';
import {
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    NotificationsOff as NotificationsOffIcon,
    NotificationsActive as NotificationsActiveIcon,
} from '@mui/icons-material';
import {
    useVapidKeyQuery,
    usePushSubscriptionQuery,
    usePushSubscribeMutation,
    usePushUnsubscribeMutation,
} from '../api/JsdhNotifikaceService/service/PushMutations';
import { useTestWebPushMutation } from '../api/JsdhNotifikaceService/service/AdminMutations';
import {
    isPushSupported,
    getPushPermissionStatus,
    requestPushPermission,
} from '../api/JsdhNotifikaceService/service/PushService';

const PushNotificationToggle: React.FC = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [permissionError, setPermissionError] = useState<string | null>(null);

    const { data: vapidKey, isLoading: vapidLoading, isError: vapidError } = useVapidKeyQuery();
    const { data: subscription, isLoading: subscriptionLoading } = usePushSubscriptionQuery();
    const subscribeMutation = usePushSubscribeMutation();
    const unsubscribeMutation = usePushUnsubscribeMutation();
    const testWebPushMutation = useTestWebPushMutation();

    const isSupported = isPushSupported();
    const permissionStatus = getPushPermissionStatus();
    const isSubscribed = !!subscription;
    const isLoading = vapidLoading || subscriptionLoading || subscribeMutation.isPending || unsubscribeMutation.isPending;
    const isTesting = testWebPushMutation.isPending;

    const handleToggleClick = () => {
        if (!isSupported) {
            setPermissionError('Váš prohlížeč nepodporuje push notifikace.');
            setDialogOpen(true);
            return;
        }

        if (!vapidKey?.enabled) {
            setPermissionError('Push notifikace nejsou na serveru povoleny.');
            setDialogOpen(true);
            return;
        }

        if (permissionStatus === 'denied') {
            setPermissionError('Push notifikace jsou v prohlížeči zablokované. Povolte je v nastavení prohlížeče.');
            setDialogOpen(true);
            return;
        }

        setPermissionError(null);
        setDialogOpen(true);
    };

    const handleSubscribe = async () => {
        try {
            // Request permission if needed
            if (permissionStatus !== 'granted') {
                const permission = await requestPushPermission();
                if (permission !== 'granted') {
                    setPermissionError('Push notifikace nebyly povoleny.');
                    return;
                }
            }

            // Subscribe - close dialog only on success
            if (vapidKey?.publicKey) {
                subscribeMutation.mutate(
                    { vapidPublicKey: vapidKey.publicKey },
                    {
                        onSuccess: () => setDialogOpen(false),
                        onError: () => setPermissionError('Nepodařilo se zapnout push notifikace.'),
                    }
                );
            }
        } catch (error) {
            setPermissionError('Nepodařilo se zapnout push notifikace.');
        }
    };

    const handleUnsubscribe = () => {
        unsubscribeMutation.mutate(undefined, {
            onSuccess: () => setDialogOpen(false),
        });
    };

    const handleTestPush = () => {
        testWebPushMutation.mutate();
    };

    const getIcon = () => {
        if (isLoading) {
            return <CircularProgress size={24} color="inherit" />;
        }
        if (!isSupported || !vapidKey?.enabled || permissionStatus === 'denied') {
            return <NotificationsOffIcon />;
        }
        if (isSubscribed) {
            return <NotificationsActiveIcon />;
        }
        return <NotificationsIcon />;
    };

    const getTooltip = () => {
        if (!isSupported) return 'Push notifikace nejsou podporovány';
        if (!vapidKey?.enabled) return 'Push notifikace nejsou povoleny';
        if (permissionStatus === 'denied') return 'Push notifikace jsou zablokované';
        if (isSubscribed) return 'Push notifikace zapnuté';
        return 'Zapnout push notifikace';
    };

    // Don't render if push is not supported or not enabled
    if (!isSupported || vapidError || (vapidKey && !vapidKey.enabled)) {
        return null;
    }

    return (
        <>
            <Tooltip title={getTooltip()}>
                <span>
                    <IconButton
                        color="inherit"
                        onClick={handleToggleClick}
                        disabled={isLoading}
                        aria-label="push notifikace"
                    >
                        {getIcon()}
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>
                    {isSubscribed ? 'Push notifikace' : 'Zapnout push notifikace?'}
                </DialogTitle>
                <DialogContent>
                    {permissionError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {permissionError}
                        </Alert>
                    ) : (
                        <DialogContentText>
                            {isSubscribed
                                ? 'Push notifikace jsou zapnuté. Můžete je otestovat nebo vypnout.'
                                : 'Zapnete push notifikace a dostanete okamžité upozornění na nový poplach přímo do prohlížeče.'}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>
                        Zavřít
                    </Button>
                    {!permissionError && isSubscribed && (
                        <Button
                            onClick={handleTestPush}
                            variant="outlined"
                            disabled={isTesting}
                        >
                            {isTesting ? 'Odesílám...' : 'Otestovat'}
                        </Button>
                    )}
                    {!permissionError && (
                        <Button
                            onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
                            variant="contained"
                            color={isSubscribed ? 'error' : 'primary'}
                            disabled={isLoading}
                        >
                            {isLoading
                                ? 'Načítám...'
                                : isSubscribed
                                    ? 'Vypnout'
                                    : 'Zapnout'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PushNotificationToggle;
